import React, { useEffect, useState } from 'react'

import { AcornsIcon } from '@acorns/icons'
import { Spinner } from '@acorns/web-components'
import {
  GenericAnalyticsClient,
  useEnv,
  useRemoteStrings,
} from '@acorns/web-utils'
import { useMutation, useQuery } from '@apollo/react-hooks'

import {
  trackSubscriptionCenterAccountReopenNextButtonTappedPayload,
  trackSubscriptionCenterAccountReopenScreenViewedPayload,
} from 'generated/trackingEvents'
import { AcceptanceDocument, User } from 'generated/types'
import { reopenFlowTypeInReopenFunnel } from 'utils/analytics/reopenFlowAnalytics'
import { CLOSED_ACCOUNT_REOPEN_ACCOUNT_BUTTON } from 'utils/testing/testIDs'

import { Agreements } from './Agreements'
import Card from './CardMessage'
import { GetStep } from './ClosedAccount'
import agreeToAcceptanceDocumentMutation from './gql/agree-to-acceptance-document.mutation.gql'
import allAcceptanceDocumentsQuery from './gql/all-acceptance-documents.query.gql'
import { ButtonContainer, StyledButton } from './styled-components'

export const REQUIRED_ACCEPTANCE_DOCUMENTS = [
  'program_agreement',
  'acorns_privacy_policy',
  'eft_agreement',
]

type AcceptanceDocumentsResponse = {
  allAcceptanceDocuments: AcceptanceDocument[]
}

type AgreeToAcceptanceDocumentsMutationType = {
  id: string
}

type Props = {
  setStep: (index: number) => void
  getStep: GetStep
  user: User
  analytics: GenericAnalyticsClient
  isLongReopen?: boolean
}

export const Confirmation = (props: Props) => {
  const [isChecked, setIsChecked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { env } = useEnv()
  const { getRemoteString } = useRemoteStrings()
  const reopenFlowType = reopenFlowTypeInReopenFunnel(props.isLongReopen)
  const REOPEN_ACCOUNT = getRemoteString({
    key: 'closed_account.confirmation.cta_title',
  })

  const [acceptanceDocuments, setAcceptanceDocuments] = useState<
    AcceptanceDocument[]
  >([])

  const { data, loading: isLoadingDocuments } = useQuery<
    AcceptanceDocumentsResponse,
    any
  >(allAcceptanceDocumentsQuery)

  useEffect(() => {
    props.analytics.track(
      ...trackSubscriptionCenterAccountReopenScreenViewedPayload({
        type: reopenFlowType,
      }),
    )
  }, [])

  useEffect(() => {
    if (data?.allAcceptanceDocuments) {
      const requiredDocuments = data.allAcceptanceDocuments.filter((document) =>
        REQUIRED_ACCEPTANCE_DOCUMENTS.includes(document.type),
      )
      setAcceptanceDocuments(requiredDocuments)
    }
  }, [data])

  const [submitAgreeToAcceptanceDocument] =
    useMutation<AgreeToAcceptanceDocumentsMutationType>(
      agreeToAcceptanceDocumentMutation,
    )
  const agreeToDocuments = () =>
    acceptanceDocuments.map(({ id }) =>
      submitAgreeToAcceptanceDocument({ variables: { id } }),
    )

  const setIsCheckedAgreement: () => void = () => {
    setIsChecked(!isChecked)
  }

  const onReopenAccountPress = async () => {
    setIsSubmitting(true)
    props.analytics.track(
      ...trackSubscriptionCenterAccountReopenNextButtonTappedPayload({
        ctaTitle: REOPEN_ACCOUNT,
        type: reopenFlowType,
      }),
    )
    await Promise.all(agreeToDocuments())
    if (props.isLongReopen) {
      window.location.assign(env.get('longReopenUrl'))
    } else {
      props.setStep(props.getStep('Pricing'))
    }
  }

  return (
    <Card
      title={getRemoteString({ key: 'closed_account.confirmation.title' })}
      icon={<AcornsIcon icon={AcornsIcon.Icon.AcornsLogo} width="32px" />}
      iconAlt="Acorns logo"
      text={getRemoteString({
        key: props.isLongReopen
          ? 'closed_account.confirmation.long_reopen.description'
          : 'closed_account.confirmation.text',
      })}
      isLoading={isLoadingDocuments}
    >
      <ButtonContainer>
        <Agreements
          isChecked={isChecked}
          onClick={setIsCheckedAgreement}
          acceptanceDocuments={acceptanceDocuments}
        />
        <StyledButton
          disabled={!isChecked || isSubmitting}
          onPress={onReopenAccountPress}
          margin="0 auto"
          data-testid={CLOSED_ACCOUNT_REOPEN_ACCOUNT_BUTTON}
        >
          {isSubmitting ? (
            <Spinner height={20} width={20} white />
          ) : (
            REOPEN_ACCOUNT
          )}
        </StyledButton>
      </ButtonContainer>
    </Card>
  )
}
